import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, AtSign, Camera, History, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cleanCashtag } from "@/lib/money";

// Minimal types for the BarcodeDetector API
declare global {
  interface Window {
    BarcodeDetector?: any;
  }
}

type ScanRecord = {
  tag: string;
  target: string;
  ts: number;
  source: "qr" | "manual";
};

const HISTORY_KEY = "cashisle.scanHistory";
const HISTORY_LIMIT = 10;

const loadHistory = (): ScanRecord[] => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((r) => r && typeof r.tag === "string" && typeof r.target === "string") : [];
  } catch {
    return [];
  }
};

const saveHistory = (entries: ScanRecord[]) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
  } catch {
    /* ignore storage errors */
  }
};

const formatTimestamp = (ts: number) => {
  const diff = Date.now() - ts;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return "just now";
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const Scan = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [manual, setManual] = useState("");
  const [history, setHistory] = useState<ScanRecord[]>(() => loadHistory());

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const recordScan = (tag: string, target: string, source: "qr" | "manual") => {
    if (!tag) return;
    setHistory((prev) => {
      const filtered = prev.filter((r) => r.tag !== tag);
      const next = [{ tag, target, ts: Date.now(), source }, ...filtered].slice(0, HISTORY_LIMIT);
      saveHistory(next);
      return next;
    });
  };

  const handleResult = (raw: string) => {
    if (!raw) return;
    let target = "";
    let tag = "";
    try {
      const u = new URL(raw);
      const m = u.pathname.match(/\/pay\/([^/?#]+)/i);
      if (m) {
        tag = cleanCashtag(decodeURIComponent(m[1]));
        target = `/pay/${tag}${u.search || ""}`;
      } else {
        tag = cleanCashtag(u.pathname.replace(/^\//, ""));
        if (tag) target = `/pay/${tag}`;
      }
    } catch {
      // legacy "cashisle:pay/tag" or plain "$tag"
      const m = raw.match(/(?:cashisle:pay\/|^\$?)([a-z0-9_]+)/i);
      if (m) {
        tag = cleanCashtag(m[1]);
        target = `/pay/${tag}`;
      }
    }
    if (!target || !tag) {
      toast.error("Unrecognised QR code");
      return;
    }
    stop();
    recordScan(tag, target, "qr");
    navigate(target);
  };


  useEffect(() => {
    const Detector = window.BarcodeDetector;
    if (!Detector) {
      setSupported(false);
      setStarting(false);
      return;
    }
    setSupported(true);
    setError(null);
    setDenied(false);
    setStarting(true);
    let cancelled = false;

    (async () => {
      try {
        const detector = new Detector({ formats: ["qr_code"] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStarting(false);

        const tick = async () => {
          if (!videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length > 0) {
              handleResult(codes[0].rawValue);
              return;
            }
          } catch { /* ignore frame errors */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        const err = e as DOMException;
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError" || /denied/i.test(err?.message || "")) {
          setDenied(true);
          setError("Camera permission denied");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setError("No camera found on this device");
        } else {
          setError(err?.message || "Camera unavailable");
        }
        setStarting(false);
      }
    })();

    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault();
    const tag = cleanCashtag(manual);
    if (tag.length < 3) { toast.error("Enter a $cashtag"); return; }
    const target = `/pay/${tag}`;
    stop();
    recordScan(tag, target, "manual");
    navigate(target);
  };

  const retry = () => {
    stop();
    setAttempt((n) => n + 1);
  };

  const rerun = (record: ScanRecord) => {
    stop();
    recordScan(record.tag, record.target, record.source);
    navigate(record.target);
  };

  const removeRecord = (tag: string) => {
    setHistory((prev) => {
      const next = prev.filter((r) => r.tag !== tag);
      saveHistory(next);
      return next;
    });
  };

  const clearHistory = () => {
    saveHistory([]);
    setHistory([]);
    toast.success("Scan history cleared");
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => { stop(); navigate(-1); }}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Scan to pay</h1>
      </BrandHeader>

      <div className="px-5 py-6 flex-1 flex flex-col gap-4">
        <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-black">
          {supported === false ? (
            <div className="absolute inset-0 grid place-items-center text-center text-muted-foreground p-6 bg-muted">
              <div>
                <Camera className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">Camera scanning isn't supported on this browser. Enter the cashtag below.</p>
              </div>
            </div>
          ) : denied ? (
            <div className="absolute inset-0 grid place-items-center text-center p-6 bg-muted">
              <div className="space-y-3 max-w-xs">
                <Camera className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm font-medium">Camera access blocked</p>
                <p className="text-xs text-muted-foreground">
                  Allow camera access in your browser settings (tap the lock icon in the address bar), then tap Retry. Or enter the merchant's cashtag below.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={retry}>Retry camera</Button>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 grid place-items-center text-center p-6 bg-muted">
              <div className="space-y-3">
                <p className="text-sm text-destructive">{error}</p>
                <Button type="button" variant="outline" size="sm" onClick={retry}>Try again</Button>
              </div>
            </div>
          ) : (
            <>
              <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover" muted playsInline />
              {starting && (
                <div className="absolute inset-0 grid place-items-center bg-black/40 text-white">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
              <div className="pointer-events-none absolute inset-8 border-2 border-white/70 rounded-2xl" />
            </>
          )}
        </div>


        <form onSubmit={submitManual} className="space-y-2">
          <p className="text-xs text-muted-foreground">Or enter the merchant's cashtag</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="cashtag"
                className="pl-9 h-11 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              />
            </div>
            <Button type="submit" variant="hero">Go</Button>
          </div>
        </form>

        <section aria-label="Recent scans" className="rounded-2xl border border-border bg-card/60 p-4">
          <header className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">Recent scans</h2>
            </div>
            {history.length > 0 && (
              <button
                type="button"
                onClick={clearHistory}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            )}
          </header>

          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Scans you complete will appear here so you can re-run them with one tap.
            </p>
          ) : (
            <ul className="space-y-2">
              {history.map((record) => (
                <li
                  key={record.tag}
                  className="flex items-center gap-2 rounded-xl bg-secondary/40 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">${record.tag}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {formatTimestamp(record.ts)} · {record.source === "qr" ? "QR scan" : "Manual"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-2"
                    onClick={() => rerun(record)}
                    aria-label={`Re-run scan for ${record.tag}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Re-run
                  </Button>
                  <button
                    type="button"
                    onClick={() => removeRecord(record.tag)}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label={`Remove ${record.tag} from history`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default Scan;
